import {useCallback, useEffect, useState} from 'react';
import {downloadReportVersionFromApi} from '../../../utils/api/reportVersions';
import {getSnapshotCache, setSnapshotCache} from '../../../utils/reportSnapshotCache';
import {shouldUsePreviewDraft} from '../../../utils/previewDraft';
import {useReportStore} from '../useReportStore';

/**
 * Hook to download and parse a report version snapshot (PM JSON).
 *
 * Loads version blob via dash-api REST.
 *
 * @param {string} reportId
 * @param {string|number} version
 * @param {string|number|null} latestVersion - When viewing latest, a staged editor draft may apply
 * @returns {{ content: Object|null, references: Array, metadata: Object|null, loading: boolean, error: Error|null, refetch: Function }}
 */
export function useVersionBlob(reportId, version, latestVersion = null) {
    const [content, setContent] = useState(null);
    const [references, setReferences] = useState([]);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const previewDraft = useReportStore((s) => s.previewDraft);
    const clearPreviewDraft = useReportStore((s) => s.clearPreviewDraft);

    const applyPreviewDraft = useCallback(() => {
        if (!shouldUsePreviewDraft(previewDraft, reportId, version, latestVersion)) {
            return false;
        }
        setContent(previewDraft.content);
        setReferences(previewDraft.references || []);
        setMetadata(previewDraft.metadata || null);
        setError(null);
        setLoading(false);
        return true;
    }, [previewDraft, reportId, version, latestVersion]);

    const fetchBlob = useCallback(async () => {
        if (!reportId || version == null) {
            setContent(null);
            setReferences([]);
            setMetadata(null);
            setLoading(false);
            setError(null);
            return;
        }

        const preferDraft = shouldUsePreviewDraft(previewDraft, reportId, version, latestVersion);
        const usedDraft = preferDraft && applyPreviewDraft();
        if (!usedDraft) {
            setLoading(true);
        }
        setError(null);

        try {
            const cached = getSnapshotCache(reportId, version);
            if (cached && !preferDraft) {
                const {references: cachedRefs, metadata: cachedMeta, ...cachedContent} = cached;
                setContent(cachedContent);
                setReferences(cachedRefs || []);
                setMetadata(cachedMeta || null);
                setError(null);
                setLoading(false);
                return;
            }

            const parsed = await downloadReportVersionFromApi(reportId, version);

            const resolvedVersion = String(parsed?.version ?? version);
            const resolvedReportId = String(parsed?.reportId ?? reportId);
            if (resolvedVersion === String(version) && resolvedReportId === String(reportId)) {
                setSnapshotCache(reportId, version, parsed);
            }

            if (!preferDraft) {
                const {references: refs, metadata: parsedMeta, ...contentData} = parsed;
                setContent(contentData);
                setReferences(refs || []);
                setMetadata(parsedMeta || null);
                setError(null);
            }
        } catch (err) {
            console.error('[useVersionBlob] Error downloading/parsing blob:', err);
            if (!preferDraft) {
                setError(err);
                setContent(null);
                setReferences([]);
                setMetadata(null);
            }
        } finally {
            setLoading(false);
        }
    }, [reportId, version, latestVersion, previewDraft, applyPreviewDraft]);

    useEffect(() => {
        fetchBlob();
    }, [fetchBlob]);

    useEffect(() => {
        if (applyPreviewDraft()) {
            return;
        }
    }, [applyPreviewDraft, previewDraft, reportId, version, latestVersion]);

    useEffect(() => {
        if (!previewDraft || !reportId || version == null || !latestVersion) {
            return;
        }
        if (String(version) !== String(latestVersion)) {
            clearPreviewDraft(reportId);
        }
    }, [previewDraft, reportId, version, latestVersion, clearPreviewDraft]);

    return {content, references, metadata, loading, error, refetch: fetchBlob};
}

/**
 * Download a specific version blob without using a hook.
 */
export async function downloadVersionBlob(reportId, version) {
    if (!reportId || version == null) {
        throw new Error('reportId and version are required');
    }
    return downloadReportVersionFromApi(reportId, version);
}
